/**
 * The preferences window's slash-command catalog (Figma `9229:190694` /
 * `9229:190738`).
 *
 * The two sections are the two providers the search box asks when the field
 * is a bare `/` (见 `command-provider` / `launch-providers`): listed commands,
 * then `/open <handle>` rows. This module only turns those rows around so the
 * spelling sits on the left and the name on the right — the same texts, the
 * other way from the dropdown.
 *
 * Recent-use order is left out on purpose. Search ranks a bare `/` by recency
 * because that box is "what you just ran"; this page is a directory, so an
 * empty history keeps `queryCommands`' alphabetical order.
 */
import type {
  LaunchItem,
  ResolvedActivityBarItem,
  ResolvedCommand,
  WhenContext,
} from '@hibro/shared';

import type { TitleResolver } from '../../commands/command-query';
import type { Translator } from '../../i18n/i18n';
import { filterLaunchItemsByWhen } from '../../layout/launch-item-when';
import { createCommandProvider } from '../../search/providers/command-provider';
import { createOpenCommandProvider } from '../../search/providers/launch-providers';
import {
  parseSearchQuery,
  type SearchProvider,
  type SearchResult,
} from '../../search/search-contract';

export interface SlashCommandRow {
  readonly key: string;
  /** What the user types, including the `/`. */
  readonly spelling: string;
  /** The command's name. Absent when it would repeat {@link spelling}. */
  readonly detail?: string;
}

export interface SlashCommandLists {
  readonly commands: readonly SlashCommandRow[];
  readonly apps: readonly SlashCommandRow[];
}

const QUERY = parseSearchQuery('/', { kind: 'all' });
const SIGNAL = new AbortController().signal;

function listed(provider: SearchProvider): readonly SearchResult[] {
  const result = provider.search(QUERY, SIGNAL);
  if (result instanceof Promise) {
    throw new Error(`${provider.id} answered asynchronously`);
  }
  return result;
}

function toRow(result: SearchResult): SlashCommandRow {
  if (result.trailing === undefined) {
    return { key: result.key, spelling: result.title };
  }
  return { key: result.key, spelling: result.trailing, detail: result.title };
}

export function collectSlashCommands(input: {
  /** Built-ins included, the same table the search box holds. */
  commands: readonly ResolvedCommand[];
  context: WhenContext;
  resolveTitle: TitleResolver;
  items: readonly LaunchItem[];
  activityBar: readonly ResolvedActivityBarItem[];
  translator: Pick<Translator, 't' | 'title'>;
}): SlashCommandLists {
  const visible = filterLaunchItemsByWhen(
    input.items,
    input.activityBar,
    input.context,
  );
  const commands = listed(
    createCommandProvider(() => ({
      commands: input.commands,
      context: input.context,
      resolveTitle: input.resolveTitle,
      mru: [],
      chordHints: new Map(),
    })),
  );
  const apps = listed(
    createOpenCommandProvider(() => ({
      items: visible,
      translator: input.translator,
    })),
  );
  return { commands: commands.map(toRow), apps: apps.map(toRow) };
}
